import cn from 'classnames'

import { useEffect, useState } from 'react'

import { countDecimalPlaces } from '../../../../utils/count-decimal-places'

import { floorNumber } from '../../../../utils/floor-number'

import { IFormattedLoan } from '../../../../store/fetchers/loans/enum'

import { formatNumberWithSpaces } from '../../../../utils/format-number-with-spaces'

import { formatNumber } from '../../../../utils/formatters'

import styles from './styles.module.scss'
import LoansHealth from './components/loans-health'
import { DashboardLoansProps } from './dashboard-loans.props'
import { useTranslation } from 'utils/use-translation'

export interface ILoanDashboardView {
  coin: string
  network: string
  color: string
  indicatorColor: string
  percent: number
  collateralAmount: number
  repaymentAmount: number
  difference: number
  differencePercent: number
}

export default function DashboardLoans({ loans, loansSummary }: DashboardLoansProps) {
  const { t } = useTranslation()
  
  const [sortedLoans, setSortedLoans] = useState<IFormattedLoan[]>([])
  const [loansView, setLoansView] = useState<ILoanDashboardView[]>([])

  const [percentDiff, setPercentDiff] = useState<number>()
  const [totalAmount, setTotalAmount] = useState<number>(0)
  const [totalRepaymentAmount, setTotalRepaymentAmount] = useState<number>(0)
  const [totalLoansAmount, setTotalLoansAmount] = useState<number>(0)

  const colors = ['line_violet', 'line_yellow', 'line_green', 'line_gray']
  const indicatorColors = ['indicator_violet', 'indicator_yellow', 'indicator_green', 'indicator_gray']

  useEffect(() => {
    if (loansSummary && loans.length) {
      sortLoans()
    }

    if (totalRepaymentAmount !== 0 && totalLoansAmount !== 0 && loansSummary && loans.length) {
      calculatePercentDiff()
    }
  }, [loansSummary, loans, totalRepaymentAmount, totalLoansAmount])

  useEffect(() => {
    if (loansView.length !== 0) {
      const amount = loansView.reduce((acc, currentLoan) => {
        return acc + Number(currentLoan.collateralAmount)
      }, 0)
      setTotalLoansAmount(amount)
    }
  }, [loansView])

  useEffect(() => {
    if (sortedLoans.length) {
      calcTotalAmountInUSD()
      calcTotalRepaymentAmountInUSD()
    }

    if (totalAmount) {
      prepareLoansView()
    }
  }, [sortedLoans, totalAmount])

  const prepareLoansView = () => {
    const loansView: ILoanDashboardView[] = []

    for (let i = 0; i < sortedLoans.length; i++) {
      let percent = (+sortedLoans[i].deposit.amount / totalAmount) * 100
      let difference = Math.abs(
        floorNumber(+sortedLoans[i].deposit.amount, 2) - floorNumber(+sortedLoans[i].repayment.amount, 2),
      )
      percent = countDecimalPlaces(percent) > 2 ? floorNumber(percent, 2) : percent
      difference = countDecimalPlaces(difference) > 2 ? Number(difference.toFixed(2)) : difference
      let differencePercent = 0

      let collateralAmount = +sortedLoans[i].deposit.amount
      collateralAmount = countDecimalPlaces(collateralAmount) > 2 ? floorNumber(collateralAmount, 2) : collateralAmount

      let repaymentAmount = +sortedLoans[i].repayment.amount
      repaymentAmount = countDecimalPlaces(repaymentAmount) > 2 ? floorNumber(repaymentAmount, 2) : repaymentAmount
      differencePercent = (difference / +sortedLoans[i].repayment.amount) * 100

      differencePercent =
        countDecimalPlaces(differencePercent) > 2 ? floorNumber(differencePercent, 2) : differencePercent

      const loanView: ILoanDashboardView = {
        color: colors[i],
        coin: sortedLoans[i].deposit.currency_code.toUpperCase(),
        network: sortedLoans[i].deposit.currency_network.toUpperCase(),
        percent,
        collateralAmount,
        repaymentAmount,
        difference,
        indicatorColor: indicatorColors[i],
        differencePercent,
      }
      loansView.push(loanView)
      if (i === 2) {
        break
      }
    }

    if (sortedLoans.length > 3) {
      let percent = 100 - loansView.reduce((acc, curr) => (acc += curr.percent), 0)
      percent = countDecimalPlaces(percent) > 2 ? floorNumber(percent, 2) : percent

      let collateralAmount = totalAmount - loansView.reduce((acc, curr) => (acc += curr.collateralAmount), 0)
      collateralAmount = countDecimalPlaces(collateralAmount) > 2 ? floorNumber(collateralAmount, 2) : collateralAmount

      let repaymentAmount = totalRepaymentAmount - loansView.reduce((acc, curr) => (acc += curr.repaymentAmount), 0)
      repaymentAmount = countDecimalPlaces(repaymentAmount) > 2 ? floorNumber(repaymentAmount, 2) : repaymentAmount

      let difference = Math.abs(collateralAmount - repaymentAmount)
      difference = countDecimalPlaces(difference) > 2 ? floorNumber(difference, 2) : difference

      let differencePercent = 0
      differencePercent = (difference * 100) / repaymentAmount

      differencePercent =
        countDecimalPlaces(differencePercent) > 2 ? floorNumber(differencePercent, 2) : differencePercent

      const loanView: ILoanDashboardView = {
        percent,
        collateralAmount,
        repaymentAmount,
        color: 'line_gray',
        coin: '',
        network: '',
        difference,
        differencePercent,
        indicatorColor: 'indicator_gray',
      }
      loansView.push(loanView)
    }
    setLoansView(loansView)
  }

  const calculatePercentDiff = () => {
    const repayment = +totalRepaymentAmount
    let percent = 0
    const difference =
      countDecimalPlaces(Math.abs(+totalLoansAmount - totalRepaymentAmount)) > 2
        ? floorNumber(Math.abs(+totalLoansAmount - totalRepaymentAmount), 2)
        : Math.abs(+totalLoansAmount - totalRepaymentAmount)
    percent = (difference / repayment) * 100
    setPercentDiff(countDecimalPlaces(percent) > 2 ? floorNumber(percent, 2) : percent)
  }

  const calcTotalAmountInUSD = () => {
    setTotalAmount(sortedLoans.reduce((acc, curr) => (acc += +curr.deposit.amount), 0))
  }

  const calcTotalRepaymentAmountInUSD = () => {
    setTotalRepaymentAmount(sortedLoans.reduce((acc, curr) => (acc += +curr.repayment.amount), 0))
  }

  const sortLoans = () => {
    const currentLoans = [...loans]
    const sumByCurrency = currentLoans
      .filter((item) => item.status === 'order_created')
      .reduce((accumulator, loan) => {
        const key = `${loan.deposit.currency_code}_${loan.deposit.currency_network}`
        const amount = +loan.deposit.amount * +loan.deposit.usdt_rate
        if (accumulator[key]) {
          accumulator[key] += amount
        } else {
          accumulator[key] = amount
        }
        return accumulator
      }, {})

    const sumByRepaymentAmount = currentLoans
      .filter((item) => item.status === 'order_created')
      .reduce((accumulator, loan) => {
        const key = `${loan.deposit.currency_code}_${loan.deposit.currency_network}`
        const repaymentAmount = +loan.repayment.amount * +loan.loan.usdt_rate

        if (accumulator[key]) {
          accumulator[key] += repaymentAmount
        } else {
          accumulator[key] = repaymentAmount
        }
        return accumulator
      }, {})
    const uniqueCurrencies = new Set()
    const updatedLoans = currentLoans
      .map((loan) => {
        if (loan.status === 'order_created') {
          const key = `${loan.deposit.currency_code}_${loan.deposit.currency_network}`
          const updatedAmount = sumByCurrency[key]
          const updatedRepaymentAmount = sumByRepaymentAmount[key]
          if (uniqueCurrencies.has(key)) {
            return null // Skip duplicate currency entries
          }
          uniqueCurrencies.add(key)
          return {
            ...loan,
            deposit: {
              ...loan.deposit,
              amount: updatedAmount,
            },
            repayment: {
              ...loan.repayment,
              amount: updatedRepaymentAmount,
            },
          }
        } else {
          return null
        }
      })
      .filter(Boolean)
    setSortedLoans(updatedLoans.sort((a, b) => +b.deposit.amount - +a.deposit.amount))
  }

  return (
    <div className={styles.loans}>
      <div className={styles['loans-data']}>
        <div className={styles['loans-data__item']}>
          <p>{t('total_collateral')}</p>
          <h5>
            {totalLoansAmount && (
              <>
                $
                {totalLoansAmount && countDecimalPlaces(+totalLoansAmount) > 2
                  ? formatNumber(floorNumber(+totalLoansAmount, 2))
                  : formatNumber(totalLoansAmount)}
              </>
            )}
          </h5>
        </div>

        <div className={styles.line}></div>

        <div className={styles['loans-data__item']}>
          <p>{t('total_repayment')}</p>
          <h5>
            {sortedLoans.length && (
              <>
                $
                {countDecimalPlaces(totalRepaymentAmount) > 2
                  ? formatNumber(floorNumber(totalRepaymentAmount, 2))
                  : formatNumber(parseFloat(totalRepaymentAmount.toString()))}
              </>
            )}
          </h5>
        </div>

        <div className={styles.line}></div>

        <div className={styles['loans-data__item']}>
          <p>{t('amount_difference')}</p>
          <h5>
            {loansSummary && (
              <>
                $
                {countDecimalPlaces(Math.abs(+totalLoansAmount - totalRepaymentAmount)) > 2
                  ? formatNumber(floorNumber(Math.abs(+totalLoansAmount - totalRepaymentAmount), 2))
                  : formatNumber(Math.abs(+totalLoansAmount - totalRepaymentAmount))}
                <span className={cn(styles.percent, styles.percent_green)}> ↑{percentDiff}%</span>
              </>
            )}
          </h5>
        </div>
      </div>

      <div className={styles['loans-lines']}>
        {loansView.map((view, index) => (
          <div key={index} style={{ width: `${view.percent}%` }} className={cn(styles.line, styles[view.color])}></div>
        ))}
      </div>

      <div className={styles['loans-coins__mobile']}>
        {loansView.map((view, index) => (
          <div key={index} className={styles.coin}>
            <div className={cn(styles.indicator, styles[view.indicatorColor])}></div>
            <p>{view.network && view.network !== '' ? view.network : t('other')}</p>
          </div>
        ))}
      </div>

      <div className={styles['loans-table-wrapper']}>
        <div className={styles['loans-table']}>
          <div className={styles['loans-table__row']}>
            <small>{t('coin')}</small>
            <small>{t('portfolio')}</small>
            <small>{t('collateral')}</small>
            <small>{t('repayment')}</small>
            <small>{t('amount_difference')}</small>
          </div>

          {loansView.map((view, index) => (
            <div key={index} className={styles['loans-table__row']}>
              <div className={styles.coin}>
                <div className={cn(styles.indicator, styles[view.indicatorColor])}></div>
                <p className={styles.oneLine}>{view.network ? view.coin + ` (${view.network})` : t('other')}</p>
              </div>
              <p>{view.percent}%</p>
              <p>${formatNumberWithSpaces(view.collateralAmount)}</p>
              <p>${formatNumberWithSpaces(view.repaymentAmount)}</p>
              <p>
                ${formatNumberWithSpaces(view.difference)}{' '}
                <span className={styles.green}>↑{view.differencePercent}%</span>
              </p>
            </div>
          ))}
        </div>
      </div>

      {loansView.map((view, index) => (
        <div key={index} className={styles.row_mobile}>
          <div className={styles.headers}>
            <h5>{t('portfolio')}</h5>
            <h5>{t('collateral_capital')}</h5>
            <h5>{t('repayment_status')}</h5>
            <h5>{t('amount_difference')}</h5>
          </div>

          <div className={styles.line}></div>

          <div className={styles.data}>
            <div className={styles.data__item}>
              <div className={styles.coin}>
                <div className={cn(styles.indicator, styles[view.indicatorColor])}></div>
                <p className={styles.coin__name}>{view.network && view.network !== '' ? view.network : t('other')}</p>
              </div>

              <p className={styles.percent}>{view.percent}%</p>
            </div>

            <div className={styles.data__item}>
              <p className={styles.balance}>${formatNumberWithSpaces(view.collateralAmount)}</p>
            </div>

            <div className={styles.data__item}>
              <p className={styles.balance}>${formatNumberWithSpaces(view.repaymentAmount)}</p>
            </div>

            <div className={styles.data__item}>
              <p className={styles.balance}>${formatNumberWithSpaces(view.difference)}</p>

              <p className={styles.green}>{'↑' + view.differencePercent}</p>
            </div>
          </div>
        </div>
      ))}

      <div className={styles.health}>
        <LoansHealth loans={loans} />
      </div>
    </div>
  )
}
